import { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { isExcludedAnalyticsEmail } from '../utils/excludedAnalyticsEmails';

export interface OrgUser {
  userId: string;
  userEmail: string;
  name?: string;
}

/**
 * Every org user (any role, active or not) for a "filter by rep" dropdown,
 * sorted by name: admins run calls and training too, and a rep who has left
 * still has history. Loaded once, and only while `enabled` (admins).
 */
export const useOrgUsers = (enabled: boolean): OrgUser[] => {
  const [users, setUsers] = useState<OrgUser[]>([]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response: any = await apiService.get('/admin/users', { limit: '500' });
        const raw = response?.data?.users ?? response?.data;
        const list: any[] = Array.isArray(raw) ? raw : [];
        const mapped: OrgUser[] = list
          .filter((u) => u && (u.firebaseUid || u.uid) && u.email && !isExcludedAnalyticsEmail(u.email))
          .map((u) => ({
            userId: u.firebaseUid || u.uid,
            userEmail: u.email,
            name: u.name,
          }))
          .sort((a, b) =>
            (a.name || a.userEmail).localeCompare(b.name || b.userEmail, undefined, {
              sensitivity: 'base',
            })
          );
        if (!cancelled) setUsers(mapped);
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return users;
};

export const orgUserLabel = (user: OrgUser): string =>
  user.name ? `${user.name} (${user.userEmail})` : user.userEmail;
